import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from './api';

const API = getApiUrl();
const LS_KEY = 'ar.dismissals';

let cache = null;
let promise = null;
const listeners = new Set();

const readLocal = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
};
const writeLocal = (items) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(items)); } catch { /* ignore */ }
};
const notify = () => listeners.forEach((fn) => fn(cache));

const isActive = (entry) => {
  if (!entry) return false;
  if (entry.mode === 'forever') return true;
  return (entry.until || '') > new Date().toISOString();
};

export async function loadDismissals() {
  if (cache) return cache;
  if (promise) return promise;
  promise = (async () => {
    try {
      const r = await axios.get(`${API}/me/dismissals`, { withCredentials: true });
      cache = r.data?.items || {};
      writeLocal(cache);
    } catch {
      cache = readLocal();
    }
    notify();
    return cache;
  })();
  return promise;
}

export function isDismissed(key) {
  const items = cache || readLocal();
  return isActive(items[key]);
}

export async function dismiss(key, mode = 'forever') {
  const now = new Date();
  const entry = mode === 'later'
    ? { mode, at: now.toISOString(), until: new Date(now.getTime() + 7 * 864e5).toISOString() }
    : { mode, at: now.toISOString() };
  cache = { ...(cache || readLocal()) };
  if (mode === 'reset') delete cache[key]; else cache[key] = entry;
  writeLocal(cache);
  notify();
  try {
    const r = await axios.post(`${API}/me/dismissals`, { key, mode }, { withCredentials: true });
    cache = r.data?.items || cache;
    writeLocal(cache);
    notify();
  } catch { /* offline: local mirror keeps it */ }
}

export function resetDismissalsCache() {
  cache = null;
  promise = null;
}

export function useDismissal(key) {
  const [ready, setReady] = useState(Boolean(cache));
  const [, force] = useState(0);

  useEffect(() => {
    const fn = () => { setReady(true); force((n) => n + 1); };
    listeners.add(fn);
    loadDismissals();
    return () => { listeners.delete(fn); };
  }, []);

  const doDismiss = useCallback((mode = 'forever') => dismiss(key, mode), [key]);
  return { ready, dismissed: isDismissed(key), dismiss: doDismiss };
}
